import React, { useState, useEffect } from 'react';
import { 
  Cog6ToothIcon, 
  FlagIcon,
  ShieldCheckIcon,
  ServerIcon,
  BellIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';

const PlatformSettings = () => {
  const [settings, setSettings] = useState(null);
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('features');

  useEffect(() => {
    fetchSettings();
    fetchFeatures();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get('/api/platform/settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchFeatures = async () => {
    try {
      const response = await axios.get('/api/platform/features');
      setFeatures(response.data);
    } catch (error) {
      console.error('Error fetching features:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async (newSettings) => {
    setSaving(true);
    try {
      await axios.put('/api/platform/settings', newSettings);
      setSettings(newSettings);
    } catch (error) {
      console.error('Error saving settings:', error);
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'features', name: 'Feature Flags', icon: FlagIcon },
    { id: 'security', name: 'Security', icon: ShieldCheckIcon },
    { id: 'limits', name: 'Rate Limits', icon: ServerIcon },
    { id: 'notifications', name: 'Notifications', icon: BellIcon }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-600 border-t-transparent"></div>
        <span className="ml-3 text-gray-600">Loading platform settings...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Settings Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8 overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="h-5 w-5 mr-2" />
                {tab.name}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Feature Flags Tab */}
      {activeTab === 'features' && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Global Feature Flags</h3>
            <p className="text-sm text-gray-500">Manage platform-wide feature availability</p>
          </div>
          <div className="divide-y divide-gray-200">
            {features.map((feature) => (
              <div key={feature.id} className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center">
                      <h4 className="text-base font-medium text-gray-900">{feature.displayName}</h4>
                      {feature.isCore && (
                        <span className="ml-2 bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
                          Core
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 mt-1">{feature.description}</p>
                    <div className="mt-2 flex items-center space-x-4 text-xs text-gray-400">
                      <span>ID: {feature.name}</span>
                      <span>•</span>
                      <span>Used by {feature.organizations?.length || 0} tenants</span>
                    </div>
                  </div>
                  <div className="ml-4">
                    <button
                      disabled={feature.isCore}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        feature.isGloballyEnabled ? 'bg-blue-600' : 'bg-gray-200'
                      } ${feature.isCore ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          feature.isGloballyEnabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Security Tab */}
      {activeTab === 'security' && (
        <div className="space-y-6">
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Authentication Settings</h3>
            </div>
            <div className="p-6">
              <div className="text-center py-8">
                <ShieldCheckIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">Security Configuration</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Platform security settings and authentication policies.
                </p>
                <div className="mt-4 text-xs text-gray-400 space-y-1">
                  <p>• JWT token expiration policies</p>
                  <p>• Multi-factor authentication requirements</p>
                  <p>• Password complexity rules</p>
                  <p>• Session timeout settings</p>
                  <p>• API rate limiting per tenant</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rate Limits Tab */}
      {activeTab === 'limits' && (
        <div className="space-y-6">
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">API Rate Limits</h3>
            </div>
            <div className="p-6">
              <div className="text-center py-8">
                <ServerIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">Rate Limiting Configuration</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Configure API rate limits and quotas for different tenant tiers.
                </p>
                <div className="mt-4 text-xs text-gray-400 space-y-1">
                  <p>• Requests per minute/hour limits</p>
                  <p>• Tier-based quota management</p>
                  <p>• Burst allowance settings</p>
                  <p>• Overage handling policies</p>
                  <p>• Real-time quota monitoring</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Resource Quotas</h3>
            </div>
            <div className="p-6">
              <div className="text-center py-8">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mx-auto">
                  <span className="text-orange-600 font-semibold">∞</span>
                </div>
                <h3 className="mt-2 text-sm font-medium text-gray-900">Storage & Resource Limits</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Database storage, file upload, and compute resource quotas.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notifications Tab */}
      {activeTab === 'notifications' && (
        <div className="space-y-6">
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">System Notifications</h3>
            </div>
            <div className="p-6">
              <div className="text-center py-8">
                <BellIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">Notification Settings</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Configure platform-wide notifications and alert settings.
                </p>
                <div className="mt-4 text-xs text-gray-400 space-y-1">
                  <p>• System health alerts</p>
                  <p>• Usage threshold notifications</p>
                  <p>• Security incident alerts</p>
                  <p>• Maintenance window announcements</p>
                  <p>• Email notification templates</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Save Indicator */}
      {saving && (
        <div className="fixed bottom-4 right-4 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg">
          <div className="flex items-center space-x-2">
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
            <span>Saving settings...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlatformSettings;